import React, { useEffect, useState } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { Card } from '../components/ui'
import { LoadingSpinner } from '../components'
import { LoadingSpinnerMeta, VerifyEmailMeta } from '../data'
import { CheckIcon, XIcon } from '../icons'

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api'

function VerifyEmail() {
    const meta = VerifyEmailMeta
    const [searchParams] = useSearchParams()
    const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading')
    const [message, setMessage] = useState('')
    const [canResend, setCanResend] = useState(false)
    const [resending, setResending] = useState(false)

    const token = searchParams.get('token')
    const email = searchParams.get('email')

    useEffect(() => {
        const verifyEmail = async () => {
            if (!token || !email) {
                setStatus('error')
                setMessage(meta.invalidLinkError)
                return
            }

            try {
                const response = await fetch(`${API_BASE_URL}/auth/verify-email`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                    },
                    credentials: 'include',
                    body: JSON.stringify({ token, email })
                })

                const data = await response.json()

                if (response.ok) {
                    setStatus('success')
                    setMessage(data.message || 'Email verified successfully!')
                } else {
                    setStatus('error')
                    setMessage(data.message || 'Verification failed. Please try again.')
                    // Allow resend if token expired
                    if (data.message?.includes('expired')) {
                        setCanResend(true)
                    }
                }
            } catch (error) {
                setStatus('error')
                setMessage('An error occurred during verification. Please try again.')
            }
        }

        verifyEmail()
    }, [token, email])

    const handleResend = async () => {
        if (!email) return

        setResending(true)
        try {
            const response = await fetch(`${API_BASE_URL}/auth/resend-verification`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({ email })
            })

            const data = await response.json()

            if (response.ok) {
                setMessage('Verification email sent! Please check your inbox.')
                setCanResend(false)
            } else {
                setMessage(data.message || 'Failed to resend verification email.')
            }
        } catch (error) {
            setMessage('An error occurred. Please try again.')
        } finally {
            setResending(false)
        }
    }

    return (
        <div className="bg-[url(/public/header_tile_gold.png)] bg-tile text-white flex flex-col items-center justify-center min-h-screen py-12 px-4 w-full">
            <Card className="w-full max-w-md text-gray-900 dark:text-white">
                <div className="text-center p-6">
                    <h2 className="text-2xl font-bold mb-6">{meta.title}</h2>

                    {status === 'loading' && (
                        <div className="flex flex-col items-center gap-4">
                            <LoadingSpinner meta={LoadingSpinnerMeta} />
                            <p className="text-gray-600 dark:text-gray-400">{meta.verifying}</p>
                        </div>
                    )}

                    {status === 'success' && (
                        <div className="flex flex-col items-center gap-4">
                            <div className="w-16 h-16 rounded-full bg-success-100 dark:bg-success-900/30 flex items-center justify-center">
                                <CheckIcon className="w-10 h-10 text-success-600 dark:text-success-400" />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">{meta.successTitle}</h3>
                            <div className="text-center space-y-3 mt-2">
                                <p className="text-gray-600 dark:text-gray-400">
                                    {meta.successMessage}
                                </p>
                                <p className="text-gray-600 dark:text-gray-400 font-medium">
                                    {meta.reviewMessage}
                                </p>
                                <p className="text-sm text-gray-500 dark:text-gray-500">
                                    {meta.approvalNotice}
                                </p>
                            </div>
                            <div className='flex items-start gap-3 mt-4 p-3 bg-primary-50 dark:bg-primary-900/20 rounded-lg'>
                                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                                    <span className="text-primary-600 dark:text-primary-400 font-bold text-sm">!</span>
                                </span>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                    {meta.travelInfo}
                                </p>
                            </div>
                            <Link
                                to="/international/login"
                                className="mt-4 inline-flex items-center justify-center px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
                            >
                                {meta.backToLogin}
                            </Link>
                        </div>
                    )}

                    {status === 'error' && (
                        <div className="flex flex-col items-center gap-4">
                            <div className="w-16 h-16 rounded-full bg-danger-100 dark:bg-danger-900/30 flex items-center justify-center">
                                <XIcon className="w-10 h-10 text-danger-600 dark:text-danger-400" />
                            </div>
                            <p className="text-gray-600 dark:text-gray-400">{message}</p>

                            {canResend && (
                                <button
                                    onClick={handleResend}
                                    disabled={resending}
                                    className="mt-4 inline-flex items-center justify-center px-6 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                                >
                                    {resending ? meta.sending : meta.resendVerification}
                                </button>
                            )}

                            <Link
                                to="/international/signup"
                                className="mt-2 text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 text-sm"
                            >
                                {meta.backToSignup}
                            </Link>
                        </div>
                    )}
                </div>
            </Card>
        </div>
    )
}

export default VerifyEmail
